/**
 * Client-side validators for the Sarah chat capture flow.
 * Mirrors the rules enforced by the submit-chat-lead edge function so users
 * get instant, friendly feedback without a network round-trip.
 */

export type ValidationOk = { ok: true; value: any; message?: undefined };
export type ValidationErr = { ok: false; value?: undefined; message: string };
export type ValidationResult = ValidationOk | ValidationErr;

const KNOWN_PAYERS = [
  "private", "self", "out of pocket", "out-of-pocket",
  "ltc", "long-term", "long term", "insurance",
  "medicaid", "medicare", "va", "veteran",
  "other",
];

export function validateName(input: string): ValidationResult {
  const trimmed = (input || "").trim();
  if (trimmed.length < 2) {
    return { ok: false, message: "Could you share the full name? Even just a first name works — at least 2 letters please." };
  }
  if (trimmed.length > 100) {
    return { ok: false, message: "That name is a bit long for our records — could you shorten it to under 100 characters?" };
  }
  // Reject obvious junk — strings of digits/symbols only
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { ok: false, message: "I want to make sure I have the right name — could you type it using letters?" };
  }
  return { ok: true, value: trimmed };
}

export function validateZip(input: string): ValidationResult {
  // Accept both 5-digit ("22030") and ZIP+4 ("22030-1234") US formats.
  // We keep the 5-digit prefix as the canonical stored value because all
  // downstream lookups (zip_cache, find-referrals, scoring) key on it.
  const trimmed = (input || "").trim();
  const match = /^(\d{5})(?:-(\d{4}))?$/.exec(trimmed);
  if (!match || match[1] === "00000") {
    return {
      ok: false,
      message: "I need a valid US ZIP code — 5 digits (22030) or ZIP+4 (22030-1234). Could you share that one more time?",
    };
  }
  return { ok: true, value: match[1] };
}

export function validateUrgency(input: string): ValidationResult {
  const n = parseInt((input || "").trim(), 10);
  if (!Number.isInteger(n) || n < 1 || n > 5) {
    return { ok: false, message: "Please give me a single whole number from 1 to 5 (1 = just exploring, 5 = need care immediately)." };
  }
  return { ok: true, value: n };
}

export function validateHours(input: string): ValidationResult {
  const n = parseFloat((input || "").trim());
  if (!Number.isFinite(n) || n < 0 || n > 168) {
    return { ok: false, message: "Hours per week should be a number between 0 and 168 (there are only 168 hours in a week 💙). Could you try again?" };
  }
  return { ok: true, value: n };
}

export function validateEmail(input: string): ValidationResult {
  const trimmed = (input || "").trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return { ok: false, message: "Please enter a valid email address so we can send your confirmation." };
  }
  return { ok: true, value: trimmed };
}

export function validatePhone(input: string): ValidationResult {
  const trimmed = (input || "").trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15) {
    return { ok: false, message: "Please enter a valid phone number (10–15 digits) for your confirmation call." };
  }
  return { ok: true, value: trimmed };
}

export function validatePayer(input: string): ValidationResult {
  const trimmed = (input || "").trim();
  if (trimmed.length < 2) {
    return { ok: false, message: "Could you share how you're planning to pay? (Private Pay, LTC Insurance, Medicaid, Medicare, VA, or Other)" };
  }
  if (trimmed.length > 100) {
    return { ok: false, message: "That answer is a bit long — could you shorten it to a payer type like Private Pay, LTC Insurance, Medicaid, Medicare, VA, or Other?" };
  }
  const lower = trimmed.toLowerCase();
  const recognised = KNOWN_PAYERS.some(k => lower.includes(k));
  if (!recognised) {
    return { ok: false, message: "I didn't recognise that payer. Could you choose one of: Private Pay, LTC Insurance, Medicaid, Medicare, VA, or Other?" };
  }
  return { ok: true, value: trimmed };
}
